// frontend/src/pages/contactpage/components/ContactForm.jsx

import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { API_BASE_URL } from '../../../utils/api';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    inquiryType: '',
    name: '',
    email: '',
    company: '',
    budget: '',
    timeline: '',
    message: '',
    skills: '',
    goals: '',
    commitment: '',
    eventType: '',
    audience: '',
    partnershipType: '',
    agreeToTerms: false
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState({ success: null, message: '' });
  const [errors, setErrors] = useState({});

  const inquiryTypes = [
    { value: 'project', label: 'Project Collaboration' },
    { value: 'mentorship', label: 'Mentorship Inquiry' },
    { value: 'speaking', label: 'Speaking Engagement' },
    { value: 'partnership', label: 'Community Partnership' }
  ];

  const budgetRanges = [
    { value: '50k-100k', label: 'FCFA50,000 - FCFA100,000' },
    { value: '100k-250k', label: 'FCFA100,000 - FCFA250,000' },
    { value: '250k-500k', label: 'FCFA250,000 - FCFA500,000' },
    { value: '500k+', label: 'FCFA500,000+' },
    { value: 'discuss', label: 'Let\'s discuss' }
  ];

  const timelineOptions = [
    { value: 'asap', label: 'ASAP (Rush project)' },
    { value: '1-2weeks', label: '1-2 weeks' },
    { value: '1month', label: '1 month' },
    { value: '2-3months', label: '2-3 months' },
    { value: '3months+', label: '3+ months' },
    { value: 'flexible', label: 'Flexible timeline' }
  ];

  const skillLevels = [
    { value: 'beginner', label: 'Beginner (0-1 years)' },
    { value: 'intermediate', label: 'Intermediate (1-3 years)' },
    { value: 'advanced', label: 'Advanced (3-5 years)' },
    { value: 'senior', label: 'Senior (5+ years)' }
  ];

  const commitmentLevels = [
    { value: '2-4hours', label: '2-4 hours per week' },
    { value: '4-8hours', label: '4-8 hours per week' },
    { value: '8-12hours', label: '8-12 hours per week' },
    { value: '12hours+', label: '12+ hours per week' }
  ];

  const eventTypes = [
    { value: 'conference', label: 'Conference Talk' },
    { value: 'workshop', label: 'Workshop/Training' },
    { value: 'podcast', label: 'Podcast Interview' },
    { value: 'webinar', label: 'Webinar' },
    { value: 'panel', label: 'Panel Discussion' }
  ];

  const audienceSizes = [
    { value: 'small', label: 'Small (10-50 people)' },
    { value: 'medium', label: 'Medium (50-200 people)' },
    { value: 'large', label: 'Large (200-500 people)' },
    { value: 'xlarge', label: 'Very Large (500+ people)' }
  ];

  const partnershipTypes = [
    { value: 'content', label: 'Content Collaboration' },
    { value: 'event', label: 'Event Partnership' },
    { value: 'mentorship', label: 'Mentorship Program' },
    { value: 'opensource', label: 'Open Source Project' },
    { value: 'community', label: 'Community Building' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.inquiryType) newErrors.inquiryType = 'Please select an inquiry type';
      if (!formData?.name?.trim()) newErrors.name = 'Name is required';
      if (!formData?.email?.trim()) newErrors.email = 'Email is required';
      if (!/\S+@\S+\.\S+/?.test(formData?.email)) newErrors.email = 'Please enter a valid email';
    }

    if (step === 2) {
      if (formData?.inquiryType === 'project') {
        if (!formData?.budget) newErrors.budget = 'Please select a budget range';
        if (!formData?.timeline) newErrors.timeline = 'Please select a timeline';
      } else if (formData?.inquiryType === 'mentorship') {
        if (!formData?.skills) newErrors.skills = 'Please select your skill level';
        if (!formData?.commitment) newErrors.commitment = 'Please select your commitment level';
      } else if (formData?.inquiryType === 'speaking') {
        if (!formData?.eventType) newErrors.eventType = 'Please select event type';
        if (!formData?.audience) newErrors.audience = 'Please select audience size';
      } else if (formData?.inquiryType === 'partnership') {
        if (!formData?.partnershipType) newErrors.partnershipType = 'Please select partnership type';
      }
    }

    if (step === 3) {
      if (!formData?.message?.trim()) newErrors.message = 'Please provide details about your inquiry';
      if (!formData?.agreeToTerms) newErrors.agreeToTerms = 'Please agree to the terms';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateStep(3)) return;

    setIsSubmitting(true);
    setFormStatus({ success: null, message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setFormStatus({ success: true, message: result.message });
        // Reset form after successful submission
        setFormData({
          inquiryType: '',
          name: '',
          email: '',
          company: '',
          budget: '',
          timeline: '',
          message: '',
          skills: '',
          goals: '',
          commitment: '',
          eventType: '',
          audience: '',
          partnershipType: '',
          agreeToTerms: false
        });
        setCurrentStep(1);
      } else {
        setFormStatus({ success: false, message: result.error || 'An unexpected error occurred.' });
      }
    } catch (error) {
      setFormStatus({ success: false, message: 'Failed to connect to the server. Please try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Let's get started</h3>
              <p className="text-muted-foreground mb-6">Tell me about yourself and what you're looking for.</p>
            </div>
            <Select
              label="What can I help you with?"
              options={inquiryTypes}
              value={formData?.inquiryType}
              onChange={(value) => handleInputChange('inquiryType', value)}
              error={errors?.inquiryType}
              required
            />
            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Full Name"
                type="text"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                error={errors?.name}
                required
              />

              <Input
                label="Email Address"
                type="email"
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                error={errors?.email}
                required
              />
            </div>
            <Input
              label="Company/Organization"
              type="text"
              value={formData?.company}
              onChange={(e) => handleInputChange('company', e?.target?.value)}
              description="Optional - helps me understand your context"
            />
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Project Details</h3>
              <p className="text-muted-foreground mb-6">Help me understand your specific needs.</p>
            </div>
            {formData?.inquiryType === 'project' && (
              <>
                <div className="grid md:grid-cols-2 gap-4">
                  <Select
                    label="Budget Range"
                    options={budgetRanges}
                    value={formData?.budget}
                    onChange={(value) => handleInputChange('budget', value)}
                    error={errors?.budget}
                    required
                  />

                  <Select
                    label="Timeline"
                    options={timelineOptions}
                    value={formData?.timeline}
                    onChange={(value) => handleInputChange('timeline', value)}
                    error={errors?.timeline}
                    required
                  />
                </div>
              </>
            )}
            {formData?.inquiryType === 'mentorship' && (
              <>
                <Select
                  label="Current Skill Level"
                  options={skillLevels}
                  value={formData?.skills}
                  onChange={(value) => handleInputChange('skills', value)}
                  error={errors?.skills}
                  required
                />

                <Input
                  label="Learning Goals"
                  type="text"
                  value={formData?.goals}
                  onChange={(e) => handleInputChange('goals', e?.target?.value)}
                  description="What specific skills or areas do you want to focus on?"
                />

                <Select
                  label="Time Commitment"
                  options={commitmentLevels}
                  value={formData?.commitment}
                  onChange={(value) => handleInputChange('commitment', value)}
                  error={errors?.commitment}
                  required
                />
              </>
            )}
            {formData?.inquiryType === 'speaking' && (
              <>
                <div className="grid md:grid-cols-2 gap-4">
                  <Select
                    label="Event Type"
                    options={eventTypes}
                    value={formData?.eventType}
                    onChange={(value) => handleInputChange('eventType', value)}
                    error={errors?.eventType}
                    required
                  />

                  <Select
                    label="Audience Size"
                    options={audienceSizes}
                    value={formData?.audience}
                    onChange={(value) => handleInputChange('audience', value)}
                    error={errors?.audience}
                    required
                  />
                </div>
              </>
            )}
            {formData?.inquiryType === 'partnership' && (
              <Select
                label="Partnership Type"
                options={partnershipTypes}
                value={formData?.partnershipType}
                onChange={(value) => handleInputChange('partnershipType', value)}
                error={errors?.partnershipType}
                required
              />
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Tell me more</h3>
              <p className="text-muted-foreground mb-6">Provide additional details about your project or inquiry.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Message <span className="text-error">*</span>
              </label>
              <textarea
                rows={6}
                value={formData?.message}
                onChange={(e) => handleInputChange('message', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                placeholder="Please provide details about your project, goals, timeline, or any specific requirements..."
              />
              {errors?.message && (
                <p className="mt-1 text-sm text-error">{errors?.message}</p>
              )}
            </div>
            <Checkbox
              label="I agree to the terms and conditions and privacy policy"
              checked={formData?.agreeToTerms}
              onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
              error={errors?.agreeToTerms}
              required
            />
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-primary mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">What happens next?</p>
                  <ul className="space-y-1">
                    <li>• I'll review your inquiry within 24 hours</li>
                    <li>• You'll receive a personalized response with next steps</li>
                    <li>• We can schedule a discovery call if needed</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Start Your Journey
          </h2>
          <p className="text-xl text-muted-foreground">
            Let's discuss how we can work together to achieve your goals.
          </p>
        </div>
        {formStatus.success !== null && (
          <div className={`p-4 mb-8 text-center rounded-lg ${formStatus.success ? 'bg-success/20 text-success' : 'bg-error/20 text-error'}`}>
            {formStatus.message}
          </div>
        )}
        <div className="bg-card rounded-2xl shadow-elegant border border-border overflow-hidden">
          {/* Progress Bar */}
          <div className="bg-muted/30 px-6 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Step {currentStep} of 3</span>
              <span className="text-sm text-muted-foreground">{Math.round((currentStep / 3) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / 3) * 100}%` }}
              ></div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 lg:p-8">
            {renderStepContent()}

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-8 border-t border-border mt-8">
              <div>
                {currentStep > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevious}
                    iconName="ArrowLeft"
                    iconPosition="left"
                  >
                    Previous
                  </Button>
                )}
              </div>

              <div>
                {currentStep < 3 ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="btn-primary"
                  >
                    Next Step
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    loading={isSubmitting}
                    iconName="Send"
                    iconPosition="right"
                    className="btn-conversion"
                  >
                    Send Message
                  </Button>
                )}
            </div>
          </div>
         </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;